import { Box } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useMemo, useRef } from 'react';
import remarkBreaks from 'remark-breaks';
import { type Components, defaultRemarkPlugins, Streamdown, type UrlTransform } from 'streamdown';

import { type CsvColumn } from '~/utils/exportRows';

import { ChatLink } from './ChatLink';
import { ExportCsvButton } from './ExportCsvButton';

// Restore the previous renderer's behavior of treating a single newline as a
// line break. Standard GFM (Streamdown's default) collapses a lone "\n" to a
// space, which runs together assistant messages that separate lines with
// single newlines rather than blank lines. remark-breaks turns soft breaks
// into <br>, matching GitHub-comment rendering. We spread the default remark
// plugins first because passing `remarkPlugins` replaces them — dropping them
// would lose GFM tables/lists.
const REMARK_PLUGINS = [...Object.values(defaultRemarkPlugins), remarkBreaks];

/**
 * Markdown renderer for AI chat / assistant messages.
 *
 * Replaces the previous hand-rolled regex renderer (markdown.ts), which only
 * understood **bold**, ATX headings, and GFM tables — so single-asterisk
 * *italics* and [links](url) leaked through as raw text. Streamdown is
 * Vercel's react-markdown drop-in for AI output: full GFM plus streaming-safe
 * handling of half-emitted markdown (unterminated **bold**, partial links).
 *
 * Security: AI/tool output is untrusted.
 *   - Raw HTML is parsed; Streamdown's hardening and the overrides below keep it from loading.
 *   - `urlTransform` (below) allows only in-app, mailto:, and https: URLs,
 *     neutralizing javascript:/data:/vbscript: vectors on links and images.
 *   - A link to another origin opens only after the reader confirms its full URL.
 *
 * Styling: this app is MUI, not Tailwind, so we do NOT rely on Streamdown's
 * default (Tailwind) classes. A wrapper Box styles the native descendant tags
 * via the MUI theme, and `components` overrides links + code blocks where a
 * real component is needed.
 */

const ALLOWED_PROTOCOLS = new Set(['https:', 'mailto:']);

const HAS_SCHEME = /^[a-z][a-z0-9+.-]*:/i;

/**
 * Allow in-app links, mailto:, and https: only. Anything else (javascript:, data:,
 * vbscript:, http:, …) is dropped, and react-markdown renders the anchor without an
 * href, so the text shows but isn't clickable.
 */
export const safeUrlTransform: UrlTransform = (url) => {
  if (!url) return undefined;
  if (!HAS_SCHEME.test(url.trim())) {
    try {
      return new URL(url, window.location.href).origin === window.location.origin ? url : undefined;
    } catch {
      return undefined;
    }
  }
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return undefined;
  }
  return ALLOWED_PROTOCOLS.has(parsed.protocol) ? url : undefined;
};

type MarkdownTableRow = { cells: string[] };

/**
 * A GFM table with a CSV export.
 *
 * The most report-shaped output the chat produces — a grouped `aggregate`
 * result — is not a widget at all: the model narrates it as a Markdown pipe
 * table (see the system prompt's table example). Without this, the one output
 * users most want in a spreadsheet is the one output they'd have to retype.
 *
 * Rows are read off the DOM on click. Streamdown renders the cells through its
 * own `thead`/`tr`/`th` components, so they are NOT reachable by walking this
 * component's children; and reading the ref during render would be null on the
 * first pass, forcing a measure-in-effect plus state. Resolving on click needs
 * neither, and is naturally correct for a table still streaming in.
 */
function MarkdownTable({ children, ...props }: { children?: React.ReactNode }) {
  const ref = useRef<HTMLTableElement>(null);

  function readRows(): MarkdownTableRow[] {
    const table = ref.current;
    if (!table) return [];
    const rows = Array.from(table.querySelectorAll('tr'))
      .map((tr) => ({
        cells: Array.from(tr.querySelectorAll('th, td')).map(
          (cell) => cell.textContent?.trim() ?? '',
        ),
      }))
      .filter((row) => row.cells.length > 0);
    // Drop the header row — it names the columns rather than being data.
    return rows.slice(1);
  }

  function readColumns(): CsvColumn<MarkdownTableRow>[] {
    const headerCells = Array.from(
      ref.current?.querySelectorAll('thead tr th, thead tr td') ?? [],
    ).map((cell) => cell.textContent?.trim() ?? '');
    return headerCells.map((label, index) => ({
      header: label,
      value: (row: MarkdownTableRow) => row.cells[index] ?? '',
    }));
  }

  return (
    <>
      <Box component="table" ref={ref} {...props}>
        {children}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: -0.5, mb: 1 }}>
        <ExportCsvButton
          getRows={readRows}
          getColumns={readColumns}
          title="Table"
          blockType="markdown-table"
        />
      </Box>
    </>
  );
}

/** react-markdown passes a `node` prop we don't want spread onto the DOM. */
function useChatComponents(): Components {
  return useMemo<Components>(
    () => ({
      a: ({ children, href }) => <ChatLink href={href}>{children}</ChatLink>,
      // Streamdown's default `strong` is a <span class="font-semibold"> — a
      // Tailwind class that's inert in this MUI app, so bold text wouldn't
      // actually render bold. Force real <strong>/<em> so the wrapper sx styles
      // them.
      strong: ({ node: _node, children, ...props }) => <strong {...props}>{children}</strong>,
      em: ({ node: _node, children, ...props }) => <em {...props}>{children}</em>,
      // Plain elements so Streamdown's Tailwind-styled CodeBlock wrapper isn't
      // used; the wrapper Box styles `pre`/`code` via sx instead.
      pre: ({ node: _node, children, ...props }) => (
        <Box component="pre" {...props}>
          {children}
        </Box>
      ),
      code: ({ node: _node, children, ...props }) => <code {...props}>{children}</code>,
      // Never load an image: its URL could carry data out without a click.
      img: ({ alt }) => (alt ? <span>{alt}</span> : null),
      source: () => null,
      picture: ({ children }) => <>{children}</>,
      video: () => null,
      audio: () => null,
      iframe: () => null,
      object: () => null,
      embed: () => null,
      table: ({ node: _node, children, ...props }) => (
        <MarkdownTable {...props}>{children}</MarkdownTable>
      ),
    }),
    [],
  );
}

export function ChatMarkdown({ children }: { children: string }) {
  const theme = useTheme();
  const components = useChatComponents();

  return (
    <Box
      sx={{
        typography: 'body2',
        lineHeight: 1.65,
        // Tighten the spacing react-markdown gives block elements so chat
        // bubbles stay compact. Streamdown wraps every block in one
        // intermediate <div>, so the first/last block elements are grandchildren
        // of this Box — target them through that wrapper.
        '& > div > :first-child': { mt: 0 },
        '& > div > :last-child': { mb: 0 },
        '& p': { my: 0.75 },
        '& strong': { fontWeight: 600, color: theme.palette.primary.dark },
        '& em': { fontStyle: 'italic' },
        '& a': { color: theme.palette.primary.main, textDecoration: 'underline' },
        '& ul, & ol': { my: 0.75, pl: 3 },
        '& li': { mb: 0.25 },
        '& blockquote': {
          my: 0.75,
          ml: 0,
          pl: 1.5,
          borderLeft: `3px solid ${alpha(theme.palette.primary.main, 0.3)}`,
          color: theme.palette.text.secondary,
        },
        '& hr': {
          border: 0,
          borderTop: `1px solid ${alpha('#000', 0.1)}`,
          my: 1.25,
        },
        // Headings: match the sizing the old renderer used.
        '& h1, & h2, & h3, & h4, & h5, & h6': {
          fontWeight: 700,
          color: theme.palette.text.primary,
          mt: 1.25,
          mb: 0.5,
          lineHeight: 1.3,
        },
        '& h1': { fontSize: '1.15rem' },
        '& h2': { fontSize: '1.05rem' },
        '& h3': { fontSize: '0.97rem' },
        '& h4, & h5, & h6': { fontSize: '0.9rem' },
        // Inline code.
        '& code': {
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: '0.85em',
          background: alpha('#000', 0.05),
          borderRadius: 0.5,
          px: 0.5,
          py: 0.125,
        },
        // Fenced code block: the inner <code> shouldn't get the inline pill.
        '& pre': {
          my: 0.75,
          p: 1.25,
          borderRadius: 1,
          background: alpha('#000', 0.05),
          overflowX: 'auto',
          fontSize: '0.8rem',
        },
        '& pre code': { background: 'none', p: 0, fontSize: 'inherit' },
        '& table': {
          borderCollapse: 'collapse',
          my: 1,
          fontSize: '0.8rem',
        },
        '& th, & td': {
          border: `1px solid ${alpha('#000', 0.1)}`,
          px: 1,
          py: 0.5,
          textAlign: 'left',
        },
        '& th': {
          background: alpha(theme.palette.primary.main, 0.06),
          fontWeight: 600,
        },
      }}
    >
      <Streamdown
        components={components}
        remarkPlugins={REMARK_PLUGINS}
        urlTransform={safeUrlTransform}
        skipHtml
        controls={false}
        linkSafety={{ enabled: false }}
      >
        {children}
      </Streamdown>
    </Box>
  );
}
